import { forwardRef, type MouseEvent, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { focusRing } from './focus-ring';

/**
 * A bookable time (design-spec §3 "Chips" + "Time-slot button").
 *
 * Sizes:
 * - sm — 29px card slot chip ("Today 16:00")
 * - md — 33px quick-pick chip on the salon book card
 * - lg — 46px time-slot button in booking step 2 (radius 12)
 *
 * States (precedence disabled > selected > soonest > default):
 * - soonest  — accent-tint fill, tint border, link text (always the first chip)
 * - selected — accent fill, white bold
 * - disabled — taken: sand-grey fill, struck through, `aria-disabled`, clicks ignored.
 *   It stays in the tab order so screen-reader users hear that the time is taken
 *   and the grid never reflows.
 *
 * Renders a `<button>` by default, or a router `<Link>` when `to` is given
 * (slot chips deep-link into the time step). A disabled link renders as a `<span>`.
 */
export type SlotChipSize = 'sm' | 'md' | 'lg';
export type SlotChipTone = 'outline' | 'sand';

export interface SlotChipProps {
  /** Visible label, e.g. "Today 16:00" or "13:00". */
  label: string;
  size?: SlotChipSize;
  /** `sand` = the borderless mobile chip. */
  tone?: SlotChipTone;
  soonest?: boolean;
  selected?: boolean;
  /** Taken / unavailable. */
  disabled?: boolean;
  /** Fuller accessible name, e.g. "Book Today 16:00 at Studio Lumen". */
  ariaLabel?: string;
  /** Internal deep link (router). */
  to?: string;
  /** Router location state for `to` (e.g. `{ fromSearch }` for "← All results"). */
  state?: unknown;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  className?: string;
}

const sizeClasses: Record<SlotChipSize, string> = {
  sm: 'h-ctl-chip px-3 text-[12.5px] rounded-full hit-44',
  md: 'h-ctl-quick px-[13px] text-[13px] rounded-full hit-44',
  lg: 'h-ctl-md px-[22px] text-[14.5px] rounded-12',
};

function stateClasses(
  size: SlotChipSize,
  tone: SlotChipTone,
  {
    soonest,
    selected,
    disabled,
  }: Pick<SlotChipProps, 'soonest' | 'selected' | 'disabled'>
): string {
  if (disabled) {
    return 'bg-sl-disabled-fill border border-sl-line text-sl-disabled-text font-medium line-through cursor-not-allowed';
  }
  if (selected) {
    return 'bg-sl-accent border border-sl-accent text-white font-bold';
  }
  if (tone === 'sand') {
    return 'bg-sl-sand border border-sl-sand text-sl-ink font-bold hover:bg-sl-sand-2';
  }
  if (soonest) {
    return 'bg-sl-accent-tint border border-sl-accent-tint-line text-sl-link font-semibold hover:border-sl-link/50';
  }
  if (size === 'lg') {
    return 'bg-sl-surface border border-sl-line-strong text-sl-ink font-semibold hover:border-sl-ink/40';
  }
  return 'bg-transparent border border-sl-line text-sl-ink font-semibold hover:border-sl-line-strong hover:bg-sl-surface';
}

export const SlotChip = forwardRef<HTMLElement, SlotChipProps>(
  function SlotChip(
    {
      label,
      size = 'sm',
      tone = 'outline',
      soonest,
      selected,
      disabled,
      ariaLabel,
      to,
      state,
      onClick,
      className,
    },
    ref
  ) {
    const { t } = useTranslation();
    const classes = cn(
      'inline-flex items-center justify-center whitespace-nowrap font-sans leading-none tabular-nums transition-colors',
      focusRing,
      sizeClasses[size],
      stateClasses(size, tone, { soonest, selected, disabled }),
      className
    );

    const name = ariaLabel ?? label;
    const accessibleName = disabled ? `${name}, ${t('ui.slot.taken')}` : name;

    if (to !== undefined) {
      if (disabled) {
        return (
          <span ref={ref as Ref<HTMLSpanElement>} className={classes}>
            {/* A role-less span can't carry aria-label/aria-disabled: say "taken" in text. */}
            <span aria-hidden="true">{label}</span>
            <span className="sr-only">{accessibleName}</span>
          </span>
        );
      }
      return (
        <Link
          ref={ref as Ref<HTMLAnchorElement>}
          to={to}
          state={state}
          className={classes}
          aria-label={ariaLabel}
          aria-current={selected ? 'true' : undefined}
          onClick={onClick}
        >
          {label}
        </Link>
      );
    }

    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        type="button"
        className={classes}
        aria-label={accessibleName !== label ? accessibleName : undefined}
        aria-pressed={selected === undefined ? undefined : selected}
        aria-disabled={disabled || undefined}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          onClick?.(e);
        }}
      >
        {label}
      </button>
    );
  }
);
